/**
 * Notification Provider
 * @public
 */
import React, { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import {
  Snackbar,
  Alert,
  Slide,
  useTheme,
  useMediaQuery,
  Fade,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
} from "@mui/icons-material";
import type { AlertColor } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { useTranslation } from "react-i18next";

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number | undefined;
  timestamp: number;
  title?: string | undefined;
}

interface NotificationContextType {
  showNotification: (
    message: string,
    severity?: AlertColor,
    duration?: number,
    title?: string
  ) => void;
  showSuccess: (message: string, duration?: number, title?: string) => void;
  showError: (message: string, duration?: number, title?: string) => void;
  showWarning: (message: string, duration?: number, title?: string) => void;
  showInfo: (message: string, duration?: number, title?: string) => void;
  showTranslatedNotification: (
    translationKey: string,
    severity?: AlertColor,
    duration?: number,
    title?: string
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

// Custom transition component for better animations
function SlideTransition(
  props: TransitionProps & { children: React.ReactElement }
) {
  const { appear, ...restProps } = props;
  return <Slide {...restProps} direction="left" appear={appear !== undefined ? appear : true} />;
}

function FadeTransition(
  props: TransitionProps & { children: React.ReactElement }
) {
  const { appear, enter, exit, in: inProp, mountOnEnter, unmountOnExit, timeout, ...restProps } = props;
  const fadeProps: any = {
    appear: appear !== undefined ? appear : true,
  };
  if (enter !== undefined) fadeProps.enter = enter;
  if (exit !== undefined) fadeProps.exit = exit;
  if (inProp !== undefined) fadeProps.in = inProp;
  if (mountOnEnter !== undefined) fadeProps.mountOnEnter = mountOnEnter;
  if (unmountOnExit !== undefined) fadeProps.unmountOnExit = unmountOnExit;
  if (timeout !== undefined) fadeProps.timeout = timeout;
  return <Fade {...restProps} {...fadeProps} />;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] =
    useState<Notification | null>(null);

  const showNotification = useCallback(
    (
      message: string,
      severity: AlertColor = "info",
      duration: number = 6000,
      title?: string
    ) => {
      const id = Math.random().toString(36).substr(2, 9);
      const notification: Notification = {
        id,
        message,
        severity,
        duration,
        timestamp: Date.now(),
        title,
      };

      setNotifications((prev) => [...prev, notification]);

      if (!currentNotification) {
        setCurrentNotification(notification);
      }
    },
    [currentNotification]
  );

  const showTranslatedNotification = useCallback(
    (
      translationKey: string,
      severity: AlertColor = "info",
      duration: number = 6000,
      title?: string
    ) => {
      const message = t(translationKey);
      showNotification(message, severity, duration, title);
    },
    [t, showNotification]
  );

  const showSuccess = useCallback(
    (message: string, duration?: number, title?: string) => {
      showNotification(message, "success", duration, title);
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string, duration?: number, title?: string) => {
      showNotification(message, "error", duration, title);
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, duration?: number, title?: string) => {
      showNotification(message, "warning", duration, title);
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, duration?: number, title?: string) => {
      showNotification(message, "info", duration, title);
    },
    [showNotification]
  );

  const handleClose = useCallback(() => {
    setCurrentNotification(null);

    // Show next notification if any
    setTimeout(() => {
      setNotifications((prev) => {
        const next = prev.slice(1);
        if (next.length > 0) {
          setCurrentNotification(next[0]);
        }
        return next;
      });
    }, 150);
  }, []);

  const getSeverityIcon = (severity: AlertColor) => {
    switch (severity) {
      case "success":
        return <CheckCircle sx={{ fontSize: 20 }} />;
      case "error":
        return <ErrorIcon sx={{ fontSize: 20 }} />;
      case "warning":
        return <Warning sx={{ fontSize: 20 }} />;
      case "info":
      default:
        return <Info sx={{ fontSize: 20 }} />;
    }
  };

  const value: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showTranslatedNotification,
  };

  const snackbarProps: any = {
    open: !!currentNotification,
    onClose: handleClose,
  };
  if (currentNotification?.duration !== undefined) {
    snackbarProps.autoHideDuration = currentNotification.duration;
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        {...snackbarProps}
        anchorOrigin={{
          vertical: isMobile ? "bottom" : "top",
          horizontal: isMobile ? "center" : "right",
        }}
        TransitionComponent={isMobile ? FadeTransition : SlideTransition}
        sx={{
          "& .MuiSnackbar-root": {
            position: "fixed",
          },
          "& .MuiSnackbarContent-root": {
            borderRadius: 2,
          },
        }}
      >
        {currentNotification ? (
          <Alert
            onClose={handleClose}
            severity={currentNotification.severity}
            variant="filled"
            icon={getSeverityIcon(currentNotification.severity)}
            sx={{
              width: "100%",
              maxWidth: isMobile ? "90vw" : 400,
              borderRadius: 2,
              boxShadow: theme.shadows[8],
              display: "flex",
              alignItems: "center",
              gap: 1,
              "& .MuiAlert-message": {
                width: "100%",
                display: "flex",
                flexDirection: currentNotification.title ? "column" : "row",
                alignItems: currentNotification.title ? "flex-start" : "center",
                justifyContent: "center",
                gap: currentNotification.title ? 0.25 : 1,
              },
              "& .MuiAlert-action": {
                alignItems: "center",
                paddingTop: 0,
                marginLeft: 0.5,
              },
              // Accents sémantiques hub → CSS vars (--color-success|warning|error)
              "&.MuiAlert-filledSuccess": {
                background:
                  "linear-gradient(135deg, var(--color-success) 0%, color-mix(in srgb, var(--color-success) 82%, #000) 100%)",
                color: "#fff",
              },
              "&.MuiAlert-filledError": {
                background:
                  "linear-gradient(135deg, var(--color-error) 0%, color-mix(in srgb, var(--color-error) 82%, #000) 100%)",
                color: "#fff",
              },
              "&.MuiAlert-filledWarning": {
                background:
                  "linear-gradient(135deg, var(--color-warning) 0%, color-mix(in srgb, var(--color-warning) 82%, #000) 100%)",
                color: "#fff",
              },
              "&.MuiAlert-filledInfo": {
                background:
                  "linear-gradient(135deg, var(--color-info) 0%, color-mix(in srgb, var(--color-info) 82%, #000) 100%)",
                color: "#fff",
              },
            }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
                sx={{
                  padding: 0.5,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {currentNotification.title && (
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                {currentNotification.title}
              </Typography>
            )}
            <Typography variant="body2">
              {currentNotification.message}
            </Typography>
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};